<template>
  <div class="invite-accept">
    <div class="invite-container">
      <div class="invite-card">

        <!-- Loading -->
        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p class="mt-3 text-muted">Validating invitation...</p>
        </div>

        <!-- Invalid token -->
        <div v-else-if="!valid" class="text-center py-5">
          <div class="icon-container mb-4">
            <i class="bi bi-x-circle text-danger"></i>
          </div>
          <h3>Invalid Invitation</h3>
          <p class="text-muted">
            {{ errorMessage || 'This invitation link is invalid or has expired.' }}
          </p>
          <router-link to="/login" class="btn btn-primary">Go to Login</router-link>
        </div>

        <!-- Valid + not authenticated -->
        <div v-else-if="!isAuthenticated" class="text-center py-4">
          <div class="icon-container mb-4">
            <i class="bi bi-envelope-check text-success"></i>
          </div>
          <h3>You're Invited!</h3>
          <p class="text-muted mb-4">
            You've been invited to join <strong>{{ tenantName }}</strong> on Equivate.
          </p>
          <p class="mb-4">Sign in to accept this invitation.</p>

          <div v-if="providersLoading" class="text-center">
            <div class="spinner-border spinner-border-sm text-secondary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>
          <div v-else-if="providers.length" class="d-flex flex-column gap-2">
            <a
              v-for="provider in providers"
              :key="provider.name"
              :href="getLoginUrl(provider.name)"
              class="btn btn-lg"
              :class="getProviderButtonClass(provider.name)"
            >
              <i :class="getProviderIcon(provider.name)" class="me-2"></i>
              Continue with {{ provider.displayName || provider.name }}
            </a>
          </div>
          <div v-else class="alert alert-info">
            No authentication providers configured.
          </div>
        </div>

        <!-- Valid + authenticated + not yet accepted -->
        <div v-else-if="!accepted" class="text-center py-4">
          <div class="icon-container mb-4">
            <i class="bi bi-person-check text-primary"></i>
          </div>
          <h3>Join {{ tenantName }}</h3>
          <p class="text-muted mb-4">
            Click below to join <strong>{{ tenantName }}</strong>.
          </p>
          <button
            class="btn btn-primary btn-lg"
            @click="acceptInvitation"
            :disabled="accepting"
          >
            <span v-if="accepting" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            {{ accepting ? 'Joining...' : 'Accept Invitation' }}
          </button>
          <div v-if="acceptError" class="alert alert-danger mt-3">
            {{ acceptError }}
          </div>
        </div>

        <!-- Success -->
        <div v-else class="text-center py-5">
          <div class="icon-container mb-4">
            <i class="bi bi-check-circle text-success"></i>
          </div>
          <h3>Welcome!</h3>
          <p class="text-muted mb-4">
            You've successfully joined <strong>{{ tenantName }}</strong>.
          </p>
          <router-link to="/" class="btn btn-primary btn-lg">Go to Dashboard</router-link>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, inject, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { UsersApi } from '@/api/users_api'
import { authService, authMutations } from '@/auth'

const route = useRoute()
const authState = inject('authState')

const loading = ref(true)
const valid = ref(false)
const tenantName = ref('')
const errorMessage = ref(null)

const providersLoading = ref(false)
const providers = ref([])

const accepting = ref(false)
const accepted = ref(false)
const acceptError = ref(null)

const isAuthenticated = computed(() => authState.isAuthenticated)
const token = computed(() => route.query.token)

const BASE_URL = process.env.VUE_APP_API_URL || 'http://localhost:8080'

onMounted(async () => {
  if (!token.value) {
    errorMessage.value = 'No invitation token provided.'
    valid.value = false
    loading.value = false
    return
  }

  try {
    const result = await UsersApi.validateInviteToken(token.value)
    if (result && result.valid) {
      valid.value = true
      tenantName.value = result.tenantName

      if (!isAuthenticated.value) {
        providersLoading.value = true
        try {
          providers.value = await authService.getProviders()
        } finally {
          providersLoading.value = false
        }
      }
    } else {
      valid.value = false
      errorMessage.value = (result && result.error) || 'Invalid or expired invitation.'
    }
  } catch (e) {
    valid.value = false
    errorMessage.value = 'Unable to validate this invitation.'
    console.error('Invite validation failed', e)
  } finally {
    loading.value = false
  }
})

async function acceptInvitation() {
  accepting.value = true
  acceptError.value = null
  try {
    await UsersApi.acceptInviteToken(token.value)
    accepted.value = true

    // Refresh auth state so the new tenant/permissions are reflected
    const status = await authService.getAuthStatus()
    if (status) {
      authMutations.setUser(status)
      if (status.activeTenant) {
        authMutations.setActiveTenant(status.activeTenant)
        authMutations.setPermissions(status.permissions)
      }
    }
  } catch (e) {
    acceptError.value = 'Failed to accept the invitation. Please try again.'
    console.error('Failed to accept invite', e)
  } finally {
    accepting.value = false
  }
}

function getLoginUrl(providerName) {
  return `${BASE_URL}/api/oauth/start?provider=${encodeURIComponent(providerName)}&inviteToken=${encodeURIComponent(token.value)}`
}

function getProviderButtonClass(providerName) {
  switch (providerName.toLowerCase()) {
    case 'google': return 'btn-outline-dark'
    case 'microsoft': return 'btn-outline-primary'
    case 'okta': return 'btn-outline-secondary'
    default: return 'btn-outline-secondary'
  }
}

function getProviderIcon(providerName) {
  switch (providerName.toLowerCase()) {
    case 'google': return 'bi bi-google'
    case 'microsoft': return 'bi bi-microsoft'
    case 'okta': return 'bi bi-shield-lock'
    default: return 'bi bi-box-arrow-in-right'
  }
}
</script>

<style scoped>
.invite-accept {
  min-height: 100vh;
  background: linear-gradient(135deg, #1a3a4a 0%, #2c6e8a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.invite-container {
  width: 100%;
  max-width: 450px;
}

.invite-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  padding: 2rem;
}

.icon-container {
  font-size: 4rem;
}

h3 {
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.btn-lg {
  padding: 0.75rem 1.5rem;
}
</style>
